import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    areAnyLoading,
    finishAlertSnippetClose,
    getQueryString,
    getReturnUrl,
    hasCartIdentifier,
    readCartSnippetParams
} from "../../Helpers/Globals";
import useServiceUrls from "../../Utils/UseServiceUrls";
import useAlert from "../../Utils/UseAlert";
import useAcknowledgeAlert from "../../Utils/UseAcknowledgeAlert";
import LoadingSpinner from "../LoadingSpinner";
import Header from "../Header";
import CloseButton from "../PageControls/CloseButton";
import AlertMessage from "./AlertMessage";

const readAlertUid = (href = window.location.href) =>
    getQueryString(href, 'bl_alertUid') ?? getQueryString(href, 'alertUid') ?? ''

export default function AlertSnippet(props) {
    const initialParams = useMemo(() => readCartSnippetParams(), [])
    const [sessionKey, setSessionKey] = useState(initialParams.sessionKey)
    const [domain, setDomain] = useState(initialParams.loginDomain)
    const [cartUid, setCartUid] = useState(initialParams.cartUid)
    const [cartExternalId, setCartExternalId] = useState(initialParams.cartExternalId)
    const [cartSourceExternalId, setCartSourceExternalId] = useState(initialParams.cartSourceExternalId)
    const [returnUrl, setReturnUrl] = useState(initialParams.returnUrl)
    const [alertUid, setAlertUid] = useState(() => readAlertUid())
    const { serviceUrlsStatus, serviceUrls, serviceUrlsError } = useServiceUrls(domain);
    const { status: alertStatus, alert, error: alertError } = useAlert(serviceUrls, sessionKey, alertUid);

    const acknowledge = useAcknowledgeAlert();
    const acknowledgedRef = useRef(false)

    useEffect(() => {
        const params = readCartSnippetParams()
        setSessionKey(params.sessionKey)
        setDomain(params.loginDomain)
        setCartUid(params.cartUid)
        setCartExternalId(params.cartExternalId)
        setCartSourceExternalId(params.cartSourceExternalId)
        setReturnUrl(params.returnUrl)
        setAlertUid(readAlertUid())
    }, [window.location.search]);

    const getAcknowledgePayload = useCallback(() => ({
        urls: serviceUrls,
        accessKey: sessionKey,
        cartUid,
        cartExternalId,
        cartSourceExternalId,
        alertUid,
    }), [serviceUrls, sessionKey, cartUid, cartExternalId, cartSourceExternalId, alertUid])

    useEffect(() => {
        if (acknowledgedRef.current) {
            return
        }
        if (!alertUid || !hasCartIdentifier(cartUid, cartExternalId, cartSourceExternalId) || !serviceUrls) {
            return
        }
        acknowledgedRef.current = true
        acknowledge.mutate(getAcknowledgePayload())
    }, [alertUid, cartUid, cartExternalId, cartSourceExternalId, serviceUrls, sessionKey, getAcknowledgePayload, acknowledge])

    const handleClose = useCallback(() => {
        finishAlertSnippetClose({
            returnUrl: returnUrl || getReturnUrl(window.location.href),
            acknowledgeAlert: alertUid && serviceUrls && acknowledge.status !== 'success'
                ? () => acknowledge.mutateAsync(getAcknowledgePayload())
                : null,
        })
    }, [alertUid, serviceUrls, acknowledge, getAcknowledgePayload, returnUrl])

    const isLoading = areAnyLoading([alertStatus, serviceUrlsStatus])

    return (
        <>
            <Header title={'Alert'}/>
            {isLoading ? <LoadingSpinner /> : null}
            {!isLoading &&
                <div className={'container'}>
                    <div className={'snippetContainer center'}>
                        {alert ? <AlertMessage message={alert.Message} /> : <p>Unable to find alert</p>}
                        <CloseButton onClose={handleClose} label={'Close'}/>
                    </div>
                </div>
            }
            {alertError ? <p className='error-text'>{alertError}</p> : null}
            {serviceUrlsError ? <p className='error-text'>{serviceUrlsError}</p> : null}
        </>
    )
}
